/**
 * Mamm Cart Extras — konsolidierte Cart-Drawer-JS-Logik
 * ============================================================================
 * Quelle für ALLE Mamm-spezifischen Cart-Drawer-JS-Module:
 *   1. mamm-delivery-estimate (Swiss-ETA-Berechnung mit Feiertagen + Sperrtagen
 *      aus den Theme-Einstellungen "Lieferzeit & Versandtage")
 *   2. mamm-item-availability (Lager-Badge pro Cart-Item)
 *   3. cart-summary Details-Pane Toggle (collapsible Aufschlüsselung)
 *   4. swipe-to-delete (Touch-Gesture Mobile, 70% Threshold + Haptic)
 *
 * Shared-Helpers (Swiss-Holiday, business-day-math) werden 1× definiert
 * statt 2× dupliziert wie vorher.
 *
 * Lifecycle-Pattern:
 *   - Init bei DOMContentLoaded
 *   - Re-Init bei cart:update / cart:updated / shopify:section:load Events
 *   - MutationObserver auf cart-items-component für robustes Re-Render
 *
 * Public API:
 *   window.MammCartExtras = {
 *     deliveryEstimate: { render, fetchAndUpdate },
 *     itemAvailability: { init },
 *   }
 * ============================================================================
 */

(function () {
  'use strict';

  // Idempotenz-Guard
  if (window.__mammCartExtrasV1__) return;
  window.__mammCartExtrasV1__ = true;

  // ─────────────────────────────────────────────────────────
  //  SHARED HELPERS — Swiss-Holiday + Business-Day-Math
  // ─────────────────────────────────────────────────────────
  // Bestellschluss: Theme-Einstellung «Bestellschluss» (Lieferzeit & Versandtage), Standard 16 Uhr (Albinot 17.10.2026)
  let CUTOFF_HOUR = 16;
  // Lieferfenster ab Versandtag = Economy 2–3 Post-Werktage (Widerspruch 5, Entscheid 15.10.2026).
  // Gilt für Warenkorb UND Liefer-Kasten auf der Produktseite, damit beide dasselbe Datum zeigen.
  // Seit 17.10.2026 (Entscheid Albinot, Variante A): Fenster von Priority frühestens (1 Werktag)
  // bis Economy spätestens (3 Werktage), weil der Kunde die Versandart erst in der Kasse wählt.
  const PRIORITY_VON = 1;
  const ECONOMY_VON = 2; // nur noch als Referenz (Economy 2–3)
  const ECONOMY_BIS = 3;
  const TIMEZONE = 'Europe/Zurich';
  const DAYS_SHORT = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
  const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
  const MAX_SHIFT_DAYS = 400;

  /**
   * Lokales YYYY-MM-DD. Bewusst NICHT toISOString(): das rechnet auf UTC um und
   * schiebt in Europe/Zurich (UTC+1/+2) jedes lokale Mitternachts-Datum auf den
   * Vortag — Feiertage werden dadurch um einen Tag verschoben erkannt.
   */
  function ymd(date) {
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${m}-${d}`;
  }

  function escapeHtml(str) {
    const el = document.createElement('div');
    el.textContent = String(str == null ? '' : str);
    return el.innerHTML;
  }

  function easterSunday(year) {
    const a = year % 19, b = Math.floor(year / 100), c = year % 100;
    const d = Math.floor(b / 4), e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4), k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(year, month - 1, day);
  }

  function getSwissHolidays(year) {
    const holidays = new Set();
    const fmt = ymd;
    const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };
    holidays.add(`${year}-01-01`);
    holidays.add(`${year}-01-02`);
    holidays.add(`${year}-05-01`);
    holidays.add(`${year}-08-01`);
    holidays.add(`${year}-12-25`);
    holidays.add(`${year}-12-26`);
    const easter = easterSunday(year);
    holidays.add(fmt(addDays(easter, -2)));
    holidays.add(fmt(addDays(easter, 1)));
    holidays.add(fmt(addDays(easter, 39)));
    holidays.add(fmt(addDays(easter, 50)));
    return holidays;
  }

  const holidayCache = {};
  function isSwissHoliday(date) {
    const year = date.getFullYear();
    if (!holidayCache[year]) holidayCache[year] = getSwissHolidays(year);
    return holidayCache[year].has(ymd(date));
  }

  /** Post-Werktag: bestimmt die Transit-Dauer. Läuft auch in unseren Betriebsferien. */
  function isBusinessDay(date) {
    const day = date.getDay();
    if (day === 0 || day === 6) return false;
    return !isSwissHoliday(date);
  }

  // ─────────────────────────────────────────────────────────
  //  SPERRTAGE — aus Theme-Einstellungen "Lieferzeit & Versandtage"
  //
  //  Bewusste Trennung von isBusinessDay:
  //    isBusinessDay → arbeitet die POST an dem Tag?  (Transit)
  //    isShippingDay → geben WIR an dem Tag ein Paket raus?  (Versand)
  //  Betriebsferien blockieren nur den Versandtag; sobald das Paket unterwegs
  //  ist, läuft der Transit normal weiter. Beides zu vermischen würde die
  //  Lieferzeit unnötig pessimistisch rechnen.
  // ─────────────────────────────────────────────────────────
  let noShipDates = new Set();
  let noShipFrom = '';
  let noShipTo = '';

  function setNoShipConfig(rawDates, rawFrom, rawTo, rawCutoff) {
    const c = parseInt(rawCutoff, 10);
    CUTOFF_HOUR = c >= 0 && c <= 24 ? c : 16;
    noShipDates = new Set(
      String(rawDates || '')
        .split(/[,;\n\r]+/)
        .map(function (s) { return s.trim(); })
        .filter(function (s) { return DATE_RE.test(s); })
    );
    const from = String(rawFrom || '').trim();
    const to = String(rawTo || '').trim();
    // Range nur bei zwei validen, nicht verdrehten Enden — sonst stillschweigend ignorieren
    const valid = DATE_RE.test(from) && DATE_RE.test(to) && from <= to;
    noShipFrom = valid ? from : '';
    noShipTo = valid ? to : '';
  }

  function isNoShipDay(date) {
    const s = ymd(date);
    if (noShipDates.has(s)) return true;
    return noShipFrom !== '' && s >= noShipFrom && s <= noShipTo;
  }

  function isShippingDay(date) {
    return isBusinessDay(date) && !isNoShipDay(date);
  }

  function nextShippingDay(date) {
    const result = new Date(date);
    // Guard: absurd lange Sperrperiode darf nicht in eine Endlosschleife laufen
    for (let i = 0; i < MAX_SHIFT_DAYS; i++) {
      if (isShippingDay(result)) return result;
      result.setDate(result.getDate() + 1);
    }
    return result;
  }

  function addBusinessDays(startDate, numDays) {
    const result = new Date(startDate);
    let added = 0;
    while (added < numDays) {
      result.setDate(result.getDate() + 1);
      if (isBusinessDay(result)) added++;
    }
    return result;
  }

  function nextBusinessDay(date) {
    const result = new Date(date);
    while (!isBusinessDay(result)) {
      result.setDate(result.getDate() + 1);
    }
    return result;
  }

  /**
   * @param {{ignoreNoShip?: boolean}} [options] ignoreNoShip = Sperrtage ausblenden.
   *        Liefert das Versanddatum, das ohne Sperrtage gälte — nur als Referenz,
   *        um zu erkennen ob ein Sperrtag den Versand tatsächlich verschiebt.
   */
  function getShipDate(options) {
    const ignoreNoShip = !!(options && options.ignoreNoShip);
    const dayOk = ignoreNoShip ? isBusinessDay : isShippingDay;
    const nextOk = ignoreNoShip ? nextBusinessDay : nextShippingDay;
    const now = new Date();
    const swissHour = parseInt(
      new Intl.DateTimeFormat('de-CH', { hour: 'numeric', hour12: false, timeZone: TIMEZONE }).format(now),
      10
    );
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (dayOk(today) && swissHour < CUTOFF_HOUR) return today;
    return nextOk(new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1));
  }

  function fmtDeliveryDate(date) {
    return `${DAYS_SHORT[date.getDay()]}, ${date.getDate()}.${date.getMonth() + 1}.`;
  }

  function getCartDrawerEl() {
    return document.querySelector('cart-drawer-component')
      || document.querySelector('#CartDrawer')
      || document.querySelector('.cart-drawer')
      || document.body;
  }

  // ═════════════════════════════════════════════════════════
  //  MODUL 1: Delivery-Estimate (.mamm-de)
  // ═════════════════════════════════════════════════════════
  const DeliveryEstimate = (function () {
    function calculate(hasPreorder, latestEta) {
      let shipDate;
      let naturalShipDate; // dasselbe ohne Sperrtage — Referenz für den Hinweis

      if (hasPreorder && latestEta) {
        const parts = latestEta.split('-');
        const etaDate = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));

        const normalShipDate = getShipDate();
        shipDate = etaDate > normalShipDate ? nextShippingDay(etaDate) : normalShipDate;

        const naturalNormal = getShipDate({ ignoreNoShip: true });
        naturalShipDate = etaDate > naturalNormal ? nextBusinessDay(etaDate) : naturalNormal;
      } else {
        shipDate = getShipDate();
        naturalShipDate = getShipDate({ ignoreNoShip: true });
      }

      return {
        shipDate: shipDate,
        // Transit ab Versandtag: Post-Werktage, Sperrtage spielen hier keine Rolle
        deliveryFrom: addBusinessDays(shipDate, PRIORITY_VON),
        deliveryTo: addBusinessDays(shipDate, ECONOMY_BIS),
        isPreorder: hasPreorder && latestEta && new Date(latestEta) > new Date(),
        etaDate: latestEta || null,
        shiftedByNoShip: shipDate.getTime() !== naturalShipDate.getTime(),
      };
    }

    function render() {
      const container = document.getElementById('mamm-delivery-estimate');
      if (!container) return;

      const hasPreorder = container.dataset.hasPreorder === 'true';
      const latestEta = container.dataset.latestEta || '';
      const preorderTitles = container.dataset.preorderTitles
        ? container.dataset.preorderTitles.split('||').filter(Boolean)
        : [];

      // Sperrtage aus den Theme-Einstellungen — vor jeder Berechnung setzen,
      // damit ein Settings-Wechsel im Theme-Editor sofort greift.
      setNoShipConfig(
        container.dataset.noshipDates,
        container.dataset.noshipFrom,
        container.dataset.noshipTo,
        container.dataset.cutoff
      );
      const noticeEnabled = container.dataset.noshipNoticeEnabled === 'true';
      const noticeTemplate = container.dataset.noshipNoticeText || '';

      const result = calculate(hasPreorder, latestEta);
      const fromStr = fmtDeliveryDate(result.deliveryFrom);
      const toStr = fmtDeliveryDate(result.deliveryTo);

      const sameDay = result.deliveryFrom.getTime() === result.deliveryTo.getTime();
      // Soll-Entwurf Warenkorb (17.10.2026): «Lieferung Di, 20.10. bis Mi, 21.10.»
      const dateDisplay = sameDay
        ? `Lieferung <span class="mamm-de__date">${fromStr}</span>`
        : `Lieferung <span class="mamm-de__date">${fromStr}</span> bis <span class="mamm-de__date">${toStr}</span>`;

      // Amber = nicht der normale Schnellweg (Vorbestellung ODER Versand verschoben)
      const dotClass = (result.isPreorder || result.shiftedByNoShip)
        ? 'mamm-de__dot--amber'
        : 'mamm-de__dot--green';

      let tooltipText;
      if (result.isPreorder) {
        // Grund sagen (Albinot 17.10.2026, wie der Kasten im alten LIVE-Warenkorb): ein Paket spart Verpackung und CO₂
        tooltipText = 'Enthält vorbestellte Artikel: Wir schicken alles zusammen in einem Paket. '
          + 'Das spart Verpackung und CO₂. '
          + 'Lieferzeit ab Versand: 2–3 Werktage (Economy), 1–2 Werktage (Priority).';
      } else if (result.shiftedByNoShip) {
        // Nicht den Bestellschluss-Satz zeigen — der wäre an einem Sperrtag schlicht falsch.
        tooltipText = 'An einzelnen Tagen versenden wir nicht (z.B. Betriebsferien). '
          + 'Deine Bestellung geht am nächsten Versandtag raus. '
          + 'Lieferzeit ab Versand: 2–3 Werktage (Economy), 1–2 Werktage (Priority).';
      } else {
        tooltipText = 'Bestellung vor ' + CUTOFF_HOUR + ':00 Uhr wird noch am selben Werktag versendet. '
          + 'Lieferzeit: 2–3 Werktage (Economy), 1–2 Werktage (Priority). '
          + 'Wochenenden und Feiertage ausgenommen.';
      }

      const infoIconSvg = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="14" height="14" aria-hidden="true">'
        + '<circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>'
        + '</svg>';

      let html = '<div class="mamm-de">'
        + '<div class="mamm-de__row">'
        + `<span class="mamm-de__dot ${dotClass}"></span>`
        + `<svg class="mamm-de__lkw ${dotClass}" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3.5 6h8.25A1.75 1.75 0 0 1 13.5 7.75V16.5H3.5A1.5 1.5 0 0 1 2 15V7.5A1.5 1.5 0 0 1 3.5 6Z"/><path d="M13.5 9.5h3.6a1.5 1.5 0 0 1 1.2.6l2.4 3.2a1.5 1.5 0 0 1 .3.9v1.3a1 1 0 0 1-1 1h-1"/><path d="M15.5 12.5h3.4"/><path d="M9.2 16.5h5.6"/><circle cx="7" cy="17" r="1.9"/><circle cx="17" cy="17" r="1.9"/><path d="M5 9.5h4.5"/></svg>`
        + `<span>${dateDisplay}</span>`
        + '<button class="mamm-de__info-btn" type="button" aria-label="Lieferinfo">'
        + infoIconSvg
        + `<span class="mamm-de__tooltip">${tooltipText}</span>`
        + '</button>'
        + '</div>';

      // Hinweis nur wenn ein Sperrtag den Versand WIRKLICH verschiebt.
      // Ein Datum ohne Erklärung wirkt wie ein schlechter Shop, mit Erklärung ehrlich.
      if (result.shiftedByNoShip && noticeEnabled) {
        const dateSpan = `<span class="mamm-de__date">${fmtDeliveryDate(result.shipDate)}</span>`;
        let template = noticeTemplate
          || 'Wir versenden gerade nicht — deine Bestellung geht am [datum] raus.';
        // "Mo, 27.7." endet bereits mit Punkt — einen direkt folgenden Satzpunkt
        // schlucken, sonst steht da "…ab Mo, 27.7.."
        template = template.split('[datum].').join('[datum]');
        // split/join statt replace: kein $-Sonderzeichen-Problem, ersetzt alle Vorkommen
        const noticeHtml = escapeHtml(template).split('[datum]').join(dateSpan);

        html += '<div class="mamm-de__noship-note">'
          + '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">'
          + '<rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4"/><path d="M8 2v4"/><path d="M3 10h18"/>'
          + '</svg>'
          + `<span>${noticeHtml}</span>`
          + '</div>';
      }

      if (result.isPreorder) {
        html += '<div class="mamm-de__preorder-note">'
          + '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">'
          + '<circle cx="12" cy="12" r="10"/><path d="M12 8v4l2 2"/>'
          + '</svg>'
          + '<span>Enthält vorbestellte Artikel — alle werden gemeinsam versandt. Spart Verpackung &amp; CO₂.</span>'
          + '</div>';
      }

      html += '</div>';
      container.innerHTML = html;
      // Kein display-Reset mehr — Skeleton-Placeholder ist visible by default,
      // wird hier durch echtes Markup ersetzt. Verhindert Flicker bei Cart-Updates.
    }

    async function fetchAndUpdate() {
      try {
        const container = document.getElementById('mamm-delivery-estimate');
        if (!container) return;
        const sectionId = document.querySelector('cart-items-component')?.dataset?.sectionId;
        if (!sectionId) { render(); return; }

        const url = `/cart?section_id=${sectionId}`;
        const htmlRes = await fetch(url, { cache: 'no-store' });
        const htmlText = await htmlRes.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlText, 'text/html');
        const fresh = doc.getElementById('mamm-delivery-estimate');

        if (fresh) {
          container.dataset.hasPreorder = fresh.dataset.hasPreorder;
          container.dataset.latestEta = fresh.dataset.latestEta || '';
          container.dataset.preorderTitles = fresh.dataset.preorderTitles || '';
          // Sperrtage mitziehen — greift Settings-Änderungen aus dem Theme-Editor ab
          container.dataset.noshipDates = fresh.dataset.noshipDates || '';
          container.dataset.noshipFrom = fresh.dataset.noshipFrom || '';
          container.dataset.noshipTo = fresh.dataset.noshipTo || '';
          container.dataset.noshipNoticeEnabled = fresh.dataset.noshipNoticeEnabled || 'false';
          container.dataset.noshipNoticeText = fresh.dataset.noshipNoticeText || '';
        }
        render();
      } catch (e) {
        // Fallback: einfach neu rendern mit bestehenden Daten
        render();
      }
    }

    /**
     * Hybrid Cart-Change Handler:
     *   1. requestAnimationFrame(render) — INSTANT render (~16ms, 1 Paint-Frame)
     *      Nutzt die data-attributes aus dem aktuellen DOM. Pebble's morphSection
     *      hat das Section bereits mit frischen Server-Daten ersetzt → DOM ist
     *      authoritative. Skeleton-Flicker praktisch unsichtbar.
     *   2. setTimeout(fetchAndUpdate, 800) — Background-Safety-Sync für
     *      Edge-Cases (z.B. wenn data-attributes stale sind).
     */
    function handleCartChange() {
      requestAnimationFrame(render);
      setTimeout(fetchAndUpdate, 800);
    }

    function init() {
      render();
      ['cart:update', 'cart:updated', 'cart:change', 'product:added', 'ajaxCart:afterUpdate'].forEach(function (ev) {
        document.addEventListener(ev, handleCartChange);
      });
      document.addEventListener('shopify:section:load', function () {
        requestAnimationFrame(render);
      });

      const drawerEl = getCartDrawerEl();
      new MutationObserver(function (mutations) {
        const relevant = mutations.some(function (m) {
          return Array.from(m.removedNodes).concat(Array.from(m.addedNodes)).some(function (n) {
            return n.nodeType === 1 && (
              n.classList?.contains('cart-items__table-row') ||
              n.querySelector?.('.cart-items__table-row') ||
              n.id === 'mamm-delivery-estimate'
            );
          });
        });
        if (relevant) handleCartChange();
      }).observe(drawerEl, { childList: true, subtree: true });
    }

    return { init: init, render: render, fetchAndUpdate: fetchAndUpdate, calculate: calculate };
  })();

  // ═════════════════════════════════════════════════════════
  //  MODUL 2: Item-Availability (.mamm-ia) — Lagerbadge pro Cart-Item
  // ═════════════════════════════════════════════════════════
  const ItemAvailability = (function () {
    function renderBadge(el) {
      const isPreorder = el.dataset.isPreorder === 'true';
      const eta = el.dataset.eta || '';

      // Apple-Pattern: Status nur zeigen wenn nicht-trivial.
      // In-Stock = Default-Erwartung des Käufers → silent (keine Badge).
      // Pre-Order = Information die eine Entscheidung beeinflusst → anzeigen.
      if (!isPreorder) {
        el.innerHTML = '';
        return;
      }

      let etaFormatted = '';
      if (eta) {
        const parts = eta.split('-');
        const etaDate = nextBusinessDay(new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10)));
        etaFormatted = fmtDeliveryDate(etaDate);
      }

      // Box Lieferzeit OS (11.09.2026): Artikel mit Lieferstatus-Metafeld (CarWash Box) sagen «Versand ab …»,
      // das Datum ist dort bereits das Versanddatum. Alle anderen Vorbestellungen behalten den bisherigen Text.
      const versandModus = el.dataset.labelMode === 'versand';
      let etaVersand = '';
      if (versandModus && eta) {
        const p = eta.split('-');
        etaVersand = fmtDeliveryDate(new Date(parseInt(p[0], 10), parseInt(p[1], 10) - 1, parseInt(p[2], 10)));
      }

      // Warenkorb (Analyse A2, Albinot 17.10.2026): an der Zeile nur «Vorbestellung», das eine Datum steht im Fuss
      const imWarenkorb = !!el.closest('cart-items-component, cart-drawer-component, .cart-page');
      const labelText = imWarenkorb
        ? 'Vorbestellung'
        : versandModus
        ? (etaVersand ? `Versand ab <span class="mamm-ia__date">${etaVersand}</span>` : 'Versandtermin folgt')
        : (etaFormatted
          ? `Vorbestellung – voraussichtlich lieferbereit ab <span class="mamm-ia__date">${etaFormatted}</span>`
          : 'Vorbestellung – Lieferdatum folgt');

      const tooltipText = versandModus
        ? ('Gerade sehr gefragt: Wir packen jede Box von Hand, sobald der Nachschub da ist. '
          + (etaVersand ? `Dein Paket geht ab ${etaVersand} raus. ` : '')
          + (imWarenkorb ? 'Alles im Warenkorb kommt zusammen in einem Paket, das spart Verpackung und CO₂. ' : '')
          + 'Stornierung jederzeit bis zum Versand möglich.')
        : ('Dieser Artikel ist noch nicht auf Lager, aber bereits beim Lieferanten bestellt. '
          + 'Dein Artikel ist reserviert und wird sofort nach Wareneingang versendet.'
          + (etaFormatted ? ` Erwarteter Wareneingang: ${etaFormatted}.` : ''));

      const iaInfoSvg = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="12" height="12" aria-hidden="true">'
        + '<circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>'
        + '</svg>';

      el.innerHTML = '<span class="mamm-ia__dot mamm-ia__dot--amber"></span>'
        + `<span class="mamm-ia__label mamm-ia__label--amber">${labelText}</span>`
        + '<button class="mamm-ia__info-btn" type="button" aria-label="Vorbestellungs-Info">'
        + iaInfoSvg
        + `<span class="mamm-ia__tooltip">${tooltipText}</span>`
        + '</button>';
    }

    function positionTooltip(btn, tooltip) {
      const rect = btn.getBoundingClientRect();
      tooltip.style.left = Math.min(
        rect.left + rect.width / 2 - 120,
        window.innerWidth - 256
      ) + 'px';
      tooltip.style.top = (rect.top - tooltip.offsetHeight - 10) + 'px';
    }

    function attachTooltips() {
      document.querySelectorAll('.mamm-ia__info-btn').forEach(function (btn) {
        if (btn.dataset.tooltipBound) return;
        btn.dataset.tooltipBound = '1';
        const tooltip = btn.querySelector('.mamm-ia__tooltip');
        if (!tooltip) return;

        btn.addEventListener('mouseenter', function () {
          tooltip.style.display = 'block';
          positionTooltip(btn, tooltip);
        });
        btn.addEventListener('mouseleave', function () {
          tooltip.style.display = 'none';
        });
        btn.addEventListener('focus', function () {
          tooltip.style.display = 'block';
          positionTooltip(btn, tooltip);
        });
        btn.addEventListener('blur', function () {
          tooltip.style.display = 'none';
        });
        btn.addEventListener('click', function (e) {
          e.stopPropagation();
          const isVisible = tooltip.style.display === 'block';
          tooltip.style.display = isVisible ? 'none' : 'block';
          if (!isVisible) positionTooltip(btn, tooltip);
        });
      });
      // Tap außerhalb schließt alle Tooltips
      document.addEventListener('click', function () {
        document.querySelectorAll('.mamm-ia__tooltip').forEach(function (t) {
          t.style.display = 'none';
        });
      });
    }

    function initAll() {
      document.querySelectorAll('.mamm-ia').forEach(renderBadge);
      attachTooltips();
    }

    function init() {
      initAll();
      ['cart:update', 'cart:updated', 'cart:change', 'product:added', 'ajaxCart:afterUpdate'].forEach(function (ev) {
        document.addEventListener(ev, function () { setTimeout(initAll, 150); });
      });
      const drawerEl = getCartDrawerEl();
      new MutationObserver(function (mutations) {
        const relevant = mutations.some(function (m) {
          return Array.from(m.addedNodes).some(function (n) {
            return n.nodeType === 1 && (n.classList.contains('mamm-ia') || n.querySelector?.('.mamm-ia'));
          });
        });
        if (relevant) setTimeout(initAll, 50);
      }).observe(drawerEl, { childList: true, subtree: true });
    }

    return { init: init };
  })();

  // ═════════════════════════════════════════════════════════
  //  MODUL 3: Cart-Summary Details-Pane Toggle
  //  (collapsible Aufschlüsselung: Zwischensumme, Rabatt, Versand, Steuern)
  // ═════════════════════════════════════════════════════════
  const CartSummaryToggle = (function () {
    function init() {
      // Document-level Click-Delegation — überlebt Pebble-Re-Renders
      document.addEventListener('click', function (e) {
        const btn = e.target.closest('.cart-summary__details-toggle');
        if (!btn) return;

        const summary = btn.closest('.cart-summary');
        if (!summary) return;

        const pane = summary.querySelector('.cart-summary__details-pane');
        if (!pane) return;

        const open = btn.getAttribute('aria-expanded') === 'true';
        const nextOpen = !open;

        btn.setAttribute('aria-expanded', String(nextOpen));
        pane.dataset.open = String(nextOpen);
      });
    }

    return { init: init };
  })();

  // ═════════════════════════════════════════════════════════
  //  MODUL 4: Swipe-to-Delete (Mobile only)
  //  Touch-Handler: links wischen → 70% Threshold → Delete
  //  Visual: rote Action-Box mit Trash-Icon hinter Row
  //  Haptic: navigator.vibrate(40) bei Trigger
  // ═════════════════════════════════════════════════════════
  const SwipeToDelete = (function () {
    const SWIPE_THRESHOLD_PCT = 0.7;
    const MIN_DETECT_PX = 8;
    const HORIZONTAL_RATIO = 1.3;
    let sharedActionBox = null;

    function isMobile() {
      return window.matchMedia('(max-width: 749px)').matches;
    }

    function ensureActionBox(parent) {
      if (sharedActionBox && sharedActionBox.parentElement === parent) return sharedActionBox;
      if (sharedActionBox && sharedActionBox.parentElement) sharedActionBox.parentElement.removeChild(sharedActionBox);
      sharedActionBox = document.createElement('div');
      sharedActionBox.className = 'cart-items__swipe-action';
      sharedActionBox.setAttribute('aria-hidden', 'true');
      sharedActionBox.innerHTML =
        '<span class="cart-items__swipe-action-label">' +
        '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
        '<polyline points="3 6 5 6 21 6"/>' +
        '<path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"/>' +
        '<path d="M10 11v6"/><path d="M14 11v6"/>' +
        '<path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/>' +
        '</svg>' +
        '<span>Entfernen</span>' +
        '</span>';
      parent.style.position = 'relative';
      parent.appendChild(sharedActionBox);
      return sharedActionBox;
    }

    function positionActionBox(row, progress) {
      const parent = row.parentElement;
      if (!parent) return;
      const box = ensureActionBox(parent);
      const rect = row.getBoundingClientRect();
      const parentRect = parent.getBoundingClientRect();
      box.style.left = (rect.left - parentRect.left) + 'px';
      box.style.top = (rect.top - parentRect.top) + 'px';
      box.style.width = rect.width + 'px';
      box.style.height = rect.height + 'px';
      box.style.display = 'flex';
      box.style.opacity = String(progress);
    }

    function hideActionBox() {
      if (!sharedActionBox) return;
      sharedActionBox.style.opacity = '0';
      setTimeout(function () {
        if (sharedActionBox) sharedActionBox.style.display = 'none';
      }, 200);
    }

    function attachToRow(row) {
      if (row.dataset.swipeBound === '1') return;
      if (!('ontouchstart' in window)) return;
      row.dataset.swipeBound = '1';

      let startX = 0;
      let startY = 0;
      let deltaX = 0;
      let isHorizontal = null;
      let rowWidth = 0;
      let active = false;

      row.addEventListener('touchstart', function (e) {
        if (!isMobile()) return;
        // Skip wenn Touch auf interaktivem Element (Quantity-Selector, Remove, Link)
        if (e.target.closest('button, a, input, select, [role="button"]')) return;
        const touch = e.touches[0];
        startX = touch.clientX;
        startY = touch.clientY;
        deltaX = 0;
        isHorizontal = null;
        rowWidth = row.offsetWidth;
        active = true;
        row.style.transition = 'none';
      }, { passive: true });

      row.addEventListener('touchmove', function (e) {
        if (!active || !isMobile()) return;
        const touch = e.touches[0];
        const dx = touch.clientX - startX;
        const dy = touch.clientY - startY;

        if (isHorizontal === null) {
          if (Math.abs(dx) < MIN_DETECT_PX && Math.abs(dy) < MIN_DETECT_PX) return;
          isHorizontal = Math.abs(dx) > Math.abs(dy) * HORIZONTAL_RATIO && dx < 0;
          if (!isHorizontal) {
            active = false;
            row.style.transform = '';
            return;
          }
        }

        if (isHorizontal) {
          e.preventDefault();
          deltaX = Math.min(0, dx);
          const progress = Math.min(1, Math.abs(deltaX) / rowWidth);
          row.style.transform = 'translateX(' + deltaX + 'px)';
          row.style.zIndex = '2';
          positionActionBox(row, progress);
        }
      }, { passive: false });

      function finishSwipe() {
        if (!active) return;
        active = false;

        if (isHorizontal === null || isHorizontal === false) {
          row.style.transform = '';
          row.style.transition = '';
          return;
        }

        const progress = Math.abs(deltaX) / rowWidth;
        row.style.transition = 'transform 0.28s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.28s ease';

        if (progress >= SWIPE_THRESHOLD_PCT) {
          row.style.transform = 'translateX(-' + rowWidth + 'px)';
          row.style.opacity = '0';
          if (navigator.vibrate) navigator.vibrate(40);
          setTimeout(function () {
            const removeBtn = row.querySelector('.cart-items__remove .cart-items__remove-button')
              || row.querySelector('.cart-items__remove-button');
            if (removeBtn) {
              removeBtn.click();
            } else {
              // Fallback: dispatch cart:update
              document.dispatchEvent(new CustomEvent('cart:update'));
            }
            hideActionBox();
          }, 280);
        } else {
          row.style.transform = '';
          setTimeout(function () {
            row.style.zIndex = '';
            row.style.transition = '';
          }, 280);
          hideActionBox();
        }
      }

      row.addEventListener('touchend', finishSwipe, { passive: true });
      row.addEventListener('touchcancel', function () {
        active = false;
        row.style.transform = '';
        row.style.transition = 'transform 0.2s ease';
        hideActionBox();
      }, { passive: true });
    }

    function initAll() {
      document.querySelectorAll('.cart-items__table-row').forEach(attachToRow);
    }

    function init() {
      if (!('ontouchstart' in window)) return;
      initAll();
      ['cart:update', 'cart:updated', 'cart:change', 'product:added'].forEach(function (ev) {
        document.addEventListener(ev, function () { setTimeout(initAll, 200); });
      });

      const drawerEl = getCartDrawerEl();
      new MutationObserver(function (mutations) {
        const newRows = mutations.some(function (m) {
          return Array.from(m.addedNodes).some(function (n) {
            return n.nodeType === 1 && (
              n.classList?.contains('cart-items__table-row') ||
              n.querySelector?.('.cart-items__table-row')
            );
          });
        });
        if (newRows) setTimeout(initAll, 50);
      }).observe(drawerEl, { childList: true, subtree: true });
    }

    return { init: init };
  })();

  // ═════════════════════════════════════════════════════════
  //  MODUL 5: Liefer-Kasten auf der Produktseite (blocks/mamm-lieferkasten.liquid)
  //  Datum «Bei dir zwischen Di, 20. und Mi, 21. Okt.»: Versandtag wie im Warenkorb
  //  (16-Uhr-Regel, Sperrtage), dann Economy 2–3 Post-Werktage. Preis rechts
  //  wechselt auf «Gratis», sobald der Warenkorb CHF 79 erreicht.
  // ═════════════════════════════════════════════════════════
  const LK_SCHWELLE = 7900;
  const LK_NAMEN = {
    de: { tag: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'], mon: ['Jan.', 'Feb.', 'März', 'Apr.', 'Mai', 'Juni', 'Juli', 'Aug.', 'Sept.', 'Okt.', 'Nov.', 'Dez.'] },
    fr: { tag: ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'], mon: ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'] },
    it: { tag: ['dom', 'lun', 'mar', 'mer', 'gio', 'ven', 'sab'], mon: ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'] },
    en: { tag: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'], mon: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] },
  };

  /** Lieferfenster ab heute: Versandtag + 1 (Priority) bis 3 (Economy) Post-Werktage. */
  function lieferFenster() {
    const ship = getShipDate();
    return { von: addBusinessDays(ship, PRIORITY_VON), bis: addBusinessDays(ship, ECONOMY_BIS) };
  }

  /** «Di, 20.» oder mit Monat «Mi, 21. Okt.» je Sprache. */
  function lkDatum(lang, d, mitMonat) {
    const n = LK_NAMEN[lang] || LK_NAMEN.de;
    const t = n.tag[d.getDay()];
    const m = mitMonat ? ' ' + n.mon[d.getMonth()] : '';
    if (lang === 'fr' || lang === 'it') return `${t} ${d.getDate()}${m}`;
    if (lang === 'en') return `${t}, ${d.getDate()}${m}`;
    return `${t}, ${d.getDate()}.${m}`;
  }

  /** «Di, 20.» / «Mi, 21. Okt.»; Monat beim ersten Datum nur, wenn er wechselt. */
  function lieferSatz(lang, satz, von, bis) {
    const gleich = von.getMonth() === bis.getMonth();
    return String(satz).split('[von]').join(lkDatum(lang, von, !gleich)).split('[bis]').join(lkDatum(lang, bis, true));
  }

  const Lieferkasten = (function () {
    function render(el) {
      const ziel = el.querySelector('[data-mlk-datum]');
      if (!ziel || ziel.hasAttribute('data-gerechnet')) return;
      // Ausland (DHL): kein Datum, die Spanne steht schon im Markup (Befund S5, 16.10.2026)
      if (!el.dataset.satz) { ziel.setAttribute('data-gerechnet', ''); return; }
      setNoShipConfig(el.dataset.noshipDates, el.dataset.noshipFrom, el.dataset.noshipTo, el.dataset.cutoff);
      const eta = el.dataset.eta || '';
      let von;
      let bis;
      if (eta) {
        // Gleiche Rechnung wie der Warenkorb mit Anlieferungsdatum (Regel Vorbestellung 16.10.2026)
        const r = DeliveryEstimate.calculate(true, eta);
        von = r.deliveryFrom;
        bis = r.deliveryTo;
        const abhol = el.querySelector('[data-mlk-abholung]');
        if (abhol && r.shipDate.getTime() > getShipDate().getTime()) {
          abhol.textContent = String(el.dataset.abSatz || '').split('[datum]').join(lkDatum(el.dataset.lang, r.shipDate, true));
        }
      } else {
        const f = lieferFenster();
        von = f.von;
        bis = f.bis;
      }
      ziel.textContent = lieferSatz(el.dataset.lang, el.dataset.satz || '', von, bis);
      ziel.setAttribute('data-gerechnet', '');
    }

    function setzeGratis(total) {
      document.querySelectorAll('mamm-lieferkasten').forEach(function (el) {
        const schwelle = parseInt(el.dataset.schwelle || '', 10) || LK_SCHWELLE;
        const gratis = total >= schwelle || parseInt(el.dataset.preis || '0', 10) >= schwelle;
        el.dataset.gratis = gratis ? 'true' : 'false';
      });
    }

    function init() {
      if (!window.customElements || window.customElements.get('mamm-lieferkasten')) return;
      window.customElements.define('mamm-lieferkasten', class extends HTMLElement {
        connectedCallback() {
          render(this);
          // Variantenwechsel: Pebble tauscht das Markup aus; dann neu rechnen
          if (!this._mlkObs) {
            this._mlkObs = new MutationObserver(() => render(this));
            this._mlkObs.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-gerechnet'] });
          }
        }
        disconnectedCallback() {
          if (this._mlkObs) { this._mlkObs.disconnect(); this._mlkObs = null; }
        }
      });
      document.addEventListener('cart:update', function (e) {
        const res = e && e.detail && e.detail.resource;
        if (res && typeof res.total_price === 'number') { setzeGratis(res.total_price); return; }
        if (!document.querySelector('mamm-lieferkasten')) return;
        fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (c) { setzeGratis(c.total_price || 0); })
          .catch(function () {});
      });
    }

    return { init: init, render: render };
  })();

  // M22 — DiscountCode Modul entfernt (UI in M10 entfernt, Codes leben im Checkout)
  // Original-Pattern: fetch /discount/CODE → cookie → /cart.js verify → refresh cart-drawer

  // ═════════════════════════════════════════════════════════
  //  Init
  // ═════════════════════════════════════════════════════════
  function init() {
    DeliveryEstimate.init();
    ItemAvailability.init();
    CartSummaryToggle.init();
    SwipeToDelete.init();
    Lieferkasten.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Public API für Debug + manuelle Re-Triggers
  window.MammCartExtras = {
    deliveryEstimate: DeliveryEstimate,
    itemAvailability: ItemAvailability,
    // Datums-Helper offengelegt für Konsolen-Debug ("warum dieses Lieferdatum?")
    // und den Node-Test unter test/delivery-dates.test.js
    dates: {
      ymd: ymd,
      isSwissHoliday: isSwissHoliday,
      isBusinessDay: isBusinessDay,
      isNoShipDay: isNoShipDay,
      isShippingDay: isShippingDay,
      nextShippingDay: nextShippingDay,
      addBusinessDays: addBusinessDays,
      getShipDate: getShipDate,
      setNoShipConfig: setNoShipConfig,
      lieferFenster: lieferFenster,
      lieferSatz: lieferSatz,
      lkDatum: lkDatum,
    },
    version: '1.4.1',
  };
})();
